import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PurchaseDocumentForm from '../components/PurchaseDocumentForm'
import SupplierPaymentForm from '../components/SupplierPaymentForm'
import ServicePurchaseForm from '../components/ServicePurchaseForm'
import { formatDate } from '../utils/dateFormat'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

// Функция для форматирования чисел с разделителями тысяч в русской локали (округление до сотых)
const formatNumber = (num: number): string => {
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface Row { 
  id: string; 
  name: string; 
  opening: number; 
  incoming: number; 
  outgoing: number; 
  closing: number;
}

const ReportsSettlements: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [lastValidFrom, setLastValidFrom] = useState('')
  const [lastValidTo, setLastValidTo] = useState('')
  
  // Загружаем сохраненный период при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('settlements-report-from')
    const savedTo = localStorage.getItem('settlements-report-to')
    if (savedFrom) setFrom(savedFrom)
    if (savedTo) setTo(savedTo)
    setIsInitialized(true)
  }, [])

  // Загружаем данные только при реальном изменении дат (не при перелистывании календаря)
  useEffect(() => {
    if (isInitialized) {
      // Проверяем, что дата действительно изменилась и является валидной
      const fromChanged = from !== lastValidFrom && from !== ''
      const toChanged = to !== lastValidTo && to !== ''
      
      // Также загружаем если обе даты пустые (сброс)
      const bothEmpty = from === '' && to === '' && (lastValidFrom !== '' || lastValidTo !== '')
      
      // Загружаем при инициализации если нет сохраненных дат
      const initialLoad = from === '' && to === '' && lastValidFrom === '' && lastValidTo === ''
      
      if (fromChanged || toChanged || bothEmpty || initialLoad) {
        // Небольшая задержка для предотвращения множественных вызовов
        const timer = setTimeout(() => {
          fetchData()
          setLastValidFrom(from)
          setLastValidTo(to)
        }, 300)
        
        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, from, to, lastValidFrom, lastValidTo])
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsTitle, setDetailsTitle] = useState('')
  const [details, setDetails] = useState<Array<{date:string;document:string;type:string;amount:number}>>([])
  const [docFormOpen, setDocFormOpen] = useState(false)
  const [docInitial, setDocInitial] = useState<any>(null)
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [inventoryItems, setInventoryItems] = useState<any[]>([])
  const [paymentFormOpen, setPaymentFormOpen] = useState(false)
  const [paymentInitial, setPaymentInitial] = useState<any>(null)
  const [servicePurchaseFormOpen, setServicePurchaseFormOpen] = useState(false)
  const [servicePurchaseInitial, setServicePurchaseInitial] = useState<any>(null)
  const [serviceProviders, setServiceProviders] = useState<any[]>([])
  const [expenseArticles, setExpenseArticles] = useState<any[]>([])

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('settlements-report-from', fromDate)
    } else {
      localStorage.removeItem('settlements-report-from')
    }
    if (toDate) {
      localStorage.setItem('settlements-report-to', toDate)
    } else {
      localStorage.removeItem('settlements-report-to')
    }
  }

  // Сброс периода
  const resetPeriod = () => {
    setFrom('')
    setTo('')
    setLastValidFrom('')
    setLastValidTo('')
    localStorage.removeItem('settlements-report-from')
    localStorage.removeItem('settlements-report-to')
  }

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const data = await apiGet<any>('/reports/settlements-movements', params)
      setRows(Array.isArray(data.items) ? data.items : [])
    } catch (e: any) {
      setError(e.message || 'Ошибка загрузки отчета')
    } finally {
      setLoading(false)
    }
  }


  const openDocByNumber = async (number: string) => {
    try {
      // Грузим вспомогательные справочники параллельно
      const [sJson, iJson] = await Promise.all([
        apiGet<any>('/suppliers', { limit: 1000 }),
        apiGet<any>('/inventory-items', { limit: 1000 })
      ])
      setSuppliers(Array.isArray(sJson.suppliers) ? sJson.suppliers : (Array.isArray(sJson.items) ? sJson.items : []))
      setInventoryItems(Array.isArray(iJson.items) ? iJson.items : [])

      // Ищем документ по номеру через поиск на бэкенде
      let found: any | undefined
      try {
        const dJson = await apiGet<any>('/purchases', { search: number, limit: 1 })
        const list = Array.isArray(dJson.purchases) ? dJson.purchases : []
        found = list.find((p: any) => p.number === number)
      } catch {}

      // Резервный вариант: берем список и ищем вручную
      if (!found) {
        try {
          const allJson = await apiGet<any>('/purchases', { limit: 1000 })
          const listAll = Array.isArray(allJson.purchases) ? allJson.purchases : []
          found = listAll.find((p: any) => p.number === number)
        } catch {}
      }

      if (found && found.id) {
        // Детально тянем документ (с позициями и поставщиком)
        let fullJson: any = null
        try {
          fullJson = await apiGet<any>(`/purchases/${found.id}`)
        } catch {}
        // Фолбэк — хотя бы «шапка», если вдруг бэкенд вернул пусто
        setDocInitial(fullJson || found)
        setDocFormOpen(true)
      }
    } catch (e) {
      // noop
    }
  }

  const openPaymentByNumber = async (number: string) => {
    try {
      // Грузим справочник поставщиков
      const sJson = await apiGet<any>('/suppliers', { limit: 1000 })
      setSuppliers(Array.isArray(sJson.suppliers) ? sJson.suppliers : [])

      // Ищем оплату по номеру
      let found: any | undefined
      try {
        const pJson = await apiGet<any>('/supplier-payments', { search: number, limit: 1 })
        const list = Array.isArray(pJson.payments) ? pJson.payments : []
        found = list.find((p: any) => p.number === number)
      } catch {}

      // Резервный вариант: берем список и ищем вручную
      if (!found) {
        try {
          const allJson = await apiGet<any>('/supplier-payments', { limit: 1000 })
          const listAll = Array.isArray(allJson.payments) ? allJson.payments : []
          found = listAll.find((p: any) => p.number === number)
        } catch {}
      }

      if (found && found.id) {
        // Детально тянем оплату
        let fullJson: any = null
        try {
          fullJson = await apiGet<any>(`/supplier-payments/${found.id}`)
        } catch {}
        setPaymentInitial(fullJson || found)
        setPaymentFormOpen(true)
      }
    } catch (e) {
      // noop
    }
  }

  const openServicePurchaseByDescription = async (description: string) => {
    try {
      // Грузим справочники параллельно
      const [spJson, eaJson] = await Promise.all([
        apiGet<any>('/service-providers'),
        apiGet<any[]>('/expense-articles')
      ])
      setServiceProviders(Array.isArray(spJson.serviceProviders) ? spJson.serviceProviders : [])
      setExpenseArticles(Array.isArray(eaJson) ? eaJson : [])

      // Извлекаем имя поставщика из описания "Закупка услуг: ARENA NATIONALA"
      const match = description.match(/Закупка услуг:\s*(.+)/)
      if (!match) return

      const providerName = match[1].trim()

      // Ищем закупку услуг по имени поставщика
      let found: any | undefined
      try {
        const spJson = await apiGet<any>('/service-purchases', { limit: 1000 })
        const list = Array.isArray(spJson.servicePurchases) ? spJson.servicePurchases : []
        found = list.find((sp: any) => sp.serviceProvider?.name === providerName)
      } catch {}

      if (found && found.id) {
        // Детально тянем закупку услуг
        let fullJson: any = null
        try {
          fullJson = await apiGet<any>(`/service-purchases/${found.id}`)
        } catch {}
        setServicePurchaseInitial(fullJson || found)
        setServicePurchaseFormOpen(true)
      }
    } catch (e) {
      // noop
    }
  }

  const openInventorySaleByNumber = async (text: string) => {
    try {
      // Извлекаем номер документа из текста "Продажа ТМЦ INV-SAL-2025-0001"
      const match = text.match(/(INV-SAL-\d{4}-\d{4})/)
      if (!match) return
      
      const number = match[1]

      // Ищем документ продажи ТМЦ по номеру
      let found: any | undefined
      try {
        const dJson = await apiGet<any>('/inventory-sales', { search: number, limit: 1 })
        const list = Array.isArray(dJson.sales) ? dJson.sales : []
        found = list.find((s: any) => s.number === number)
      } catch {}

      // Резервный вариант: берем список и ищем вручную
      if (!found) {
        try {
          const allJson = await apiGet<any>('/inventory-sales', { limit: 1000 })
          const listAll = Array.isArray(allJson.sales) ? allJson.sales : []
          found = listAll.find((s: any) => s.number === number)
        } catch {}
      }

      if (found && found.id) {
        // Переходим на страницу продаж ТМЦ с открытием документа для редактирования
        navigate(`/inventory-sales?open=${found.id}`)
      }
    } catch (e) {
      // noop
    }
  }

  const total = rows.reduce((acc, r) => ({
    opening: acc.opening + r.opening,
    incoming: acc.incoming + r.incoming,
    outgoing: acc.outgoing + r.outgoing,
    closing: acc.closing + r.closing,
  }), { opening: 0, incoming: 0, outgoing: 0, closing: 0 })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Взаиморасчеты с контрагентами</h2>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Дата с" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Дата по" />
          <button onClick={() => { savePeriod(from, to); fetchData(); }} className="px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border">Обновить</button>
          <button onClick={resetPeriod} className="px-3 py-2 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 border">Сбросить</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">{error}</div>}
      {loading ? <p className="text-gray-600">Загрузка...</p> : (
        <div className="overflow-x-auto bg-white shadow rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Контрагент</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Нач. долг</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ПРИХОД</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Расход</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кон. долг</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                  setDetailsOpen(true)
                  setDetailsTitle(r.name)
                  try {
                    const params: Record<string, string> = {}
                    if (from) params.from = from
                    if (to) params.to = to
                    const data = await apiGet<any>(`/reports/settlements-movements/${r.id}/details`, params)
                    setDetails(Array.isArray(data.items) ? data.items : [])
                  } catch (e) {
                    setDetails([])
                  }
                }}>
                  <td className="px-4 py-2 text-sm text-gray-900 text-left">{r.name}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r.opening)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r.incoming)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r.outgoing)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r.closing)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 text-base font-bold">
                <td className="px-4 py-2 font-bold">Итого</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.opening)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.incoming)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.outgoing)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {detailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-30" onClick={() => setDetailsOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Детализация: {detailsTitle}</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setDetailsOpen(false)}>✕</button>
            </div>
            <div className="p-4 overflow-x-auto overflow-y-auto flex-1">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Основание</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Приход</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Расход</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Строка итого */}
                  {(() => {
                    let totalIncoming = 0
                    let totalOutgoing = 0
                    
                    details.forEach(d => {
                      const amount = Number(d.amount)
                      const document = d.document || ''
                      
                      // Оплаты поставщикам показываем в ПРИХОД (хотя сумма отрицательная)
                      if (document.includes('Оплата поставщику') || document.includes('PAY-SUP-')) {
                        if (amount < 0) totalIncoming += Math.abs(amount)
                      }
                      // Задолженность по закупке НЕ показываем в ПРИХОД
                      else if (document.includes('Задолженность по закупке') || document.includes('INV-PUR-')) {
                        if (amount > 0) totalOutgoing += amount
                      }
                      // Закупка услуг НЕ показываем в ПРИХОД
                      else if (document.includes('Закупка услуг')) {
                        if (amount > 0) totalOutgoing += amount
                      }
                      // Остальные положительные суммы в ПРИХОД
                      else if (amount > 0) {
                        totalIncoming += amount
                      }
                      // Остальные отрицательные суммы в РАСХОД
                      else if (amount < 0) {
                        totalOutgoing += Math.abs(amount)
                      }
                    })
                    
                    return (
                      <tr className="bg-gray-100 font-semibold sticky top-8 z-10">
                        <td className="px-3 py-2 text-sm text-gray-900 font-bold">ИТОГО</td>
                        <td className="px-3 py-2 text-sm text-gray-900 font-bold"></td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-bold">
                          {totalIncoming > 0 ? formatNumber(totalIncoming) : '-'}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-bold">
                          {totalOutgoing > 0 ? formatNumber(totalOutgoing) : '-'}
                        </td>
                      </tr>
                    )
                  })()}
                  {details
                    .sort((a, b) => {
                      // Сортировка по дате: старее - выше (возрастающий порядок)
                      const dateA = new Date(a.date)
                      const dateB = new Date(b.date)
                      return dateA.getTime() - dateB.getTime()
                    })
                    .map((d, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 text-sm text-gray-700">{formatDate(d.date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {(() => {
                          const text = d.document || ''
                          // Если вся ячейка — это номер документа закупки, делаем целиком кликабельным
                          if (/^INV-PUR-\d{4}-\d{4}$/.test(text)) {
                            return (
                              <button
                                className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={(e) => { e.stopPropagation(); openDocByNumber(text) }}
                              >
                                {text}
                              </button>
                            )
                          }
                          // Если вся ячейка — это номер оплаты поставщику, делаем целиком кликабельным
                          if (/^PAY-SUP-\d{4}-\d{4}$/.test(text)) {
                            return (
                              <button
                                className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={(e) => { e.stopPropagation(); openPaymentByNumber(text) }}
                              >
                                {text}
                              </button>
                            )
                          }
                          // Если вся ячейка — это номер продажи ТМЦ, делаем целиком кликабельным
                          if (/^INV-SAL-\d{4}-\d{4}$/.test(text)) {
                            return (
                              <button
                                className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={(e) => { e.stopPropagation(); openInventorySaleByNumber(text) }}
                              >
                                {text}
                              </button>
                            )
                          }
                          // Ищем номер закупки внутри текста
                          const purchaseMatch = text.match(/(INV-PUR-\d{4}-\d{4})/)
                          if (purchaseMatch) {
                            const num = purchaseMatch[1]
                            const [before, after] = text.split(num)
                            return (
                              <span>
                                {before}
                                <button
                                  className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                  onClick={(e) => { e.stopPropagation(); openDocByNumber(num) }}
                                >
                                  {num}
                                </button>
                                {after}
                              </span>
                            )
                          }
                          // Ищем номер оплаты поставщику внутри текста
                          const paymentMatch = text.match(/(PAY-SUP-\d{4}-\d{4})/)
                          if (paymentMatch) {
                            const num = paymentMatch[1]
                            const [before, after] = text.split(num)
                            return (
                              <span>
                                {before}
                                <button
                                  className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                  onClick={(e) => { e.stopPropagation(); openPaymentByNumber(num) }}
                                >
                                  {num}
                                </button>
                                {after}
                              </span>
                            )
                          }
                          // Ищем номер продажи ТМЦ внутри текста
                          const saleMatch = text.match(/(INV-SAL-\d{4}-\d{4})/)
                          if (saleMatch) {
                            const num = saleMatch[1]
                            const [before, after] = text.split(num)
                            return (
                              <span>
                                {before}
                                <button
                                  className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                  onClick={(e) => { e.stopPropagation(); openInventorySaleByNumber(num) }}
                                >
                                  {num}
                                </button>
                                {after}
                              </span>
                            )
                          }
                          // "Закупка услуг:" - делаем кликабельным
                          if (text.startsWith('Закупка услуг:')) {
                            return (
                              <button
                                className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={(e) => { e.stopPropagation(); openServicePurchaseByDescription(text) }}
                              >
                                {text}
                              </button>
                            )
                          }
                          // "Продажа ТМЦ" - делаем кликабельным
                          if (text.startsWith('Продажа ТМЦ')) {
                            return (
                              <button
                                className="text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={(e) => { e.stopPropagation(); openInventorySaleByNumber(text) }}
                              >
                                {text}
                              </button>
                            )
                          }
                          return text
                        })()}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                        {(() => {
                          const amount = Number(d.amount)
                          const document = d.document || ''
                          // Оплаты поставщикам показываем в ПРИХОД (хотя сумма отрицательная)
                          if (document.includes('Оплата поставщику') || document.includes('PAY-SUP-')) {
                            return amount < 0 ? formatNumber(Math.abs(amount)) : '-'
                          }
                          // Задолженность по закупке НЕ показываем в ПРИХОД
                          if (document.includes('Задолженность по закупке') || document.includes('INV-PUR-')) {
                            return '-'
                          }
                          // Закупка услуг НЕ показываем в ПРИХОД
                          if (document.includes('Закупка услуг')) {
                            return '-'
                          }
                          // Остальные положительные суммы в ПРИХОД
                          return amount > 0 ? formatNumber(amount) : '-'
                        })()}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                        {(() => {
                          const amount = Number(d.amount)
                          const document = d.document || ''
                          // Оплаты поставщикам НЕ показываем в РАСХОД
                          if (document.includes('Оплата поставщику') || document.includes('PAY-SUP-')) {
                            return '-'
                          }
                          // Задолженность по закупке показываем в РАСХОД (положительные суммы)
                          if (document.includes('Задолженность по закупке') || document.includes('INV-PUR-')) {
                            return amount > 0 ? formatNumber(amount) : '-'
                          }
                          // Закупка услуг показываем в РАСХОД (положительные суммы)
                          if (document.includes('Закупка услуг')) {
                            return amount > 0 ? formatNumber(amount) : '-'
                          }
                          // Остальные отрицательные суммы в РАСХОД
                          return amount < 0 ? formatNumber(Math.abs(amount)) : '-'
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end">
              <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={() => setDetailsOpen(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      {docFormOpen && (
        <PurchaseDocumentForm
          title={`Редактировать документ ${docInitial?.number || ''}`}
          suppliers={suppliers}
          inventoryItems={inventoryItems}
          initial={docInitial}
          onCancel={() => { setDocFormOpen(false); setDocInitial(null) }}
          onSubmit={async (payload) => {
            try {
              await apiPut(`/purchases/${docInitial.id}`, payload)
              setDocFormOpen(false)
              setDocInitial(null)
              fetchData()
            } catch (e) {}
          }}
        />
      )}

      {paymentFormOpen && (
        <SupplierPaymentForm
          title={`Редактировать оплату ${paymentInitial?.number || ''}`}
          suppliers={suppliers}
          initial={paymentInitial}
          onCancel={() => { setPaymentFormOpen(false); setPaymentInitial(null) }}
          onSubmit={async (payload) => {
            try {
              await apiPut(`/supplier-payments/${paymentInitial.id}`, payload)
              setPaymentFormOpen(false)
              setPaymentInitial(null)
              fetchData()
            } catch (e) {}
          }}
        />
      )}

      {servicePurchaseFormOpen && (
        <ServicePurchaseForm
          title={`Редактировать закупку услуг ${servicePurchaseInitial?.serviceProvider?.name || ''}`}
          serviceProviders={serviceProviders}
          expenseArticles={expenseArticles}
          initial={servicePurchaseInitial}
          onCancel={() => { setServicePurchaseFormOpen(false); setServicePurchaseInitial(null) }}
          onSubmit={async (payload) => {
            try {
              await apiPut(`/service-purchases/${servicePurchaseInitial.id}`, payload)
              setServicePurchaseFormOpen(false)
              setServicePurchaseInitial(null)
              fetchData()
            } catch (e) {}
          }}
        />
      )}

    </div>
  )
}

export default ReportsSettlements


